/**
 * Pure mapping from a just-finished Terra Draw feature + the current draw style
 * to a BFF annotation create-request (ADR-034 Inc 3b + 4; feature-spec §6 / §8).
 * Pure so it's CI-testable; the Terra Draw drawing lifecycle that produces the
 * feature is WebGL-only (verified in the deferred §5.11 visual pass).
 *
 * Cross-references:
 *   - ADR-034; feature-spec §6 (shapes) / §8 (interaction).
 *   - @/annotations/annotation-tool-store (the draw style) / use-annotations
 *     (the API request shape).
 */
import distance from "@turf/distance";
import type { GeoJSONStoreFeatures } from "terra-draw";
import type { AnnotationDrawStyle } from "@/annotations/annotation-tool-store";
import type { AnnotationWriteRequest } from "@/annotations/use-annotations";

type AnnotationStyle = AnnotationWriteRequest["style"];

/** The minimum bounding-stroke width (px) — the ``AnnotationStyle.stroke_width_px``
 *  schema floor (ge=1). Area shapes (circle, polygon) default their outline to this
 *  hairline on draw (operator request 2026-06-12 — the fill carries the shape, the
 *  outline just defines the edge); the operator can thicken it in the Entity Details
 *  panel. Lines keep the draw-style weight (the line *is* the mark). */
const MIN_STROKE_WIDTH_PX = 1;

/** Map the ephemeral draw style (camelCase) → the API annotation style (snake_case). */
export function drawStyleToWriteStyle(style: AnnotationDrawStyle): AnnotationStyle {
  return {
    stroke_color: style.strokeColor,
    stroke_width_px: style.strokeWidthPx,
    fill_color: style.fillColor,
    fill_opacity: style.fillOpacity,
    marker_color: style.markerColor,
    marker_size_px: style.markerSizePx,
  };
}

/**
 * Derive a circle's canonical centre (``[lon, lat]``) + radius (metres) from a
 * drawn Terra Draw circle ring. Terra Draw renders a circle as a ``Polygon``
 * ring; we store it canonically as centre + ``radius_m`` (feature-spec §4 / §6).
 * Centre = the ring's vertex centroid; radius = the geodesic centre→vertex
 * distance (the actual drawn radius — no reliance on Terra Draw internals).
 */
export function circleRingToCentreRadius(ring: readonly number[][]): {
  centre: [number, number];
  radiusM: number;
} {
  const first = ring[0];
  const last = ring[ring.length - 1];
  // Drop the closing duplicate vertex if present.
  const vertices = last[0] === first[0] && last[1] === first[1] ? ring.slice(0, -1) : ring;
  let sumLon = 0;
  let sumLat = 0;
  for (const [lon, lat] of vertices) {
    sumLon += lon;
    sumLat += lat;
  }
  const centre: [number, number] = [sumLon / vertices.length, sumLat / vertices.length];
  return { centre, radiusM: distance(centre, first, { units: "meters" }) };
}

/**
 * Build a create-request from a just-finished Terra Draw feature. Handles the
 * draw modes wired so far — ``point`` → marker (Inc 3b), ``linestring`` → line
 * (Inc 3b), ``circle`` → circle (centre + radius, Inc 4), ``polygon`` → polygon
 * (Inc 4) — returning ``null`` for any other mode (e.g. the static ``render``
 * mode, which is never "finished").
 */
export function finishedFeatureToRequest(
  feature: GeoJSONStoreFeatures,
  style: AnnotationDrawStyle,
): AnnotationWriteRequest | null {
  const mode = feature.properties.mode;
  const geometry = feature.geometry;
  if (mode === "point" && geometry.type === "Point") {
    return {
      kind: "marker",
      geometry: { type: "Point", coordinates: geometry.coordinates },
      style: drawStyleToWriteStyle(style),
    };
  }
  if (mode === "linestring" && geometry.type === "LineString") {
    return {
      kind: "line",
      geometry: { type: "LineString", coordinates: geometry.coordinates },
      // Line defaults (ADR-034 batch 2): a dotted stroke + a triangle arrowhead
      // at the finishing (last-clicked) end. The operator can change both in the
      // Entity Details panel.
      style: { ...drawStyleToWriteStyle(style), stroke_dash: "dotted", arrow_end: "triangle" },
    };
  }
  if (mode === "circle" && geometry.type === "Polygon") {
    const { centre, radiusM } = circleRingToCentreRadius(geometry.coordinates[0]);
    return {
      kind: "circle",
      geometry: { type: "Point", coordinates: centre },
      radius_m: radiusM,
      // Area shapes default to the hairline outline — the fill carries the shape.
      style: { ...drawStyleToWriteStyle(style), stroke_width_px: MIN_STROKE_WIDTH_PX },
    };
  }
  if (mode === "polygon" && geometry.type === "Polygon") {
    return {
      kind: "polygon",
      geometry: { type: "Polygon", coordinates: geometry.coordinates },
      // Area shapes default to the hairline outline — the fill carries the shape.
      style: { ...drawStyleToWriteStyle(style), stroke_width_px: MIN_STROKE_WIDTH_PX },
    };
  }
  return null;
}
